import { useState, memo, ChangeEvent, useEffect } from 'react';
import * as Styled from './multi-select.styles';
import { useTranslation } from 'react-i18next';
import { Input } from '@innowise-group/mui-kit';
import { LabeledCheckbox } from '@shared-mui-components';
import { type FilterSelectProps } from '../../../../types';

export interface MultiSelectDropdownProps
  extends Pick<FilterSelectProps, 'state' | 'search' | 'singleSelect' | 'commentIds' | 'onValueChange'> {
  anchorEl: HTMLElement | null;
  // Display order of the options, the order of the state keys by default
  optionIds?: string[];
  // Once this many options are selected, the others are locked
  maxSelected?: number;
  // Shown instead of an empty list
  emptyText?: string;
  // Minimal distance to the window edges, 16px by default
  marginThreshold?: number;
  // CSS width of the dropdown, 350px by default
  width?: string;
  // `isEmpty`: nothing is selected and nothing is typed in the search
  onClose: (isEmpty: boolean) => void;
}

export const deselectAll = (state: FilterSelectProps['state']): FilterSelectProps['state'] =>
  Object.entries(state).reduce((acc, [key, val]) => ({ ...acc, [key]: { ...val, active: false } }), {});

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  anchorEl,
  state,
  search,
  singleSelect,
  commentIds,
  optionIds,
  maxSelected,
  emptyText,
  marginThreshold,
  width,
  onValueChange,
  onClose,
}) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string>('');
  const [expandedItem, setCurrentExpandedItem] = useState('');

  const { selected, rest, total } = (optionIds || Object.keys(state)).reduce(
    (acc, id) => {
      const { title, value, active } = state[id] || {};
      if (!title?.toLowerCase().startsWith(filter.toLowerCase())) return acc;

      const key = active ? 'selected' : 'rest';
      return {
        ...acc,
        total: acc.total + 1,
        [key]: { count: acc[key].count + 1, ids: [...acc[key].ids, value] },
      };
    },
    {
      selected: {
        count: 0,
        ids: [],
      },
      rest: { count: 0, ids: [] },
      total: 0,
    },
  );

  const isEmpty = !selected.count && !filter;
  const selectedCount = Object.values(state).filter((option) => option?.active).length;
  const isLocked = singleSelect ? !!selected.ids.length : !!maxSelected && selectedCount >= maxSelected;

  useEffect(() => {
    if (!anchorEl) setCurrentExpandedItem('');
  }, [anchorEl]);

  const onFilterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value);

  const onClickHandler = (id: string) => (e: Pick<Event, 'stopPropagation'>) => {
    e.stopPropagation();
    onValueChange({ ...state, [id]: { ...state[id], active: !state[id].active, comment: '' } });
  };

  const toggleItemDetails = (id: string) => () => {
    setCurrentExpandedItem((prev) => (prev !== id ? id : ''));
  };

  const onItemDetailsInputChange = (id: string) => (e: ChangeEvent<HTMLInputElement>) => {
    onValueChange({ ...state, [id]: { ...state[id], comment: e.target.value } });
  };

  const onReset = () => {
    onValueChange(deselectAll(state));
  };

  const onCloseHandler = () => {
    onClose(isEmpty);
    setFilter('');
  };

  return (
    <Styled.CustomAnimationWrapper
      disablePortal={true}
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onCloseHandler}
      {...(marginThreshold !== undefined && { marginThreshold })}
    >
      <Styled.FilterWrapper $width={width}>
        {search ? (
          <Styled.InputContainer>
            <Styled.CustomInput placeholder={t('pages.search')} onChange={onFilterInputChange} value={filter} />
          </Styled.InputContainer>
        ) : (
          <Styled.EmptyInputContainer />
        )}
        <Styled.VariantsList>
          {emptyText && !total ? <Styled.EmptyText>{emptyText}</Styled.EmptyText> : null}
          <Styled.PreselectedVariantsGroupContainer>
            {selected.ids.map((id) => (
              <Styled.VariantWrapper key={id}>
                <Styled.VariantContainer onClick={toggleItemDetails(id)}>
                  <LabeledCheckbox
                    onChange={onClickHandler(id)}
                    defaultChecked={!!state[id].active}
                    readOnly
                    id={id}
                    label={state[id].title}
                  />
                  {commentIds?.includes(id) && (
                    <Styled.CustomExpandIcon
                      expanded={expandedItem === id}
                      size={17}
                      viewBox="5 5 13 13"
                      type="u_angle-down"
                    />
                  )}
                </Styled.VariantContainer>
                {expandedItem === id && commentIds?.includes(id) && (
                  <Styled.CommentWrapper>
                    <Input
                      value={state[id]?.comment || ''}
                      onChange={onItemDetailsInputChange(id)}
                      placeholder={t('filters.union.sourceDetailsPlaceholder')}
                    />
                  </Styled.CommentWrapper>
                )}
              </Styled.VariantWrapper>
            ))}
          </Styled.PreselectedVariantsGroupContainer>
          <Styled.CustomDivider />
          <Styled.UnselectedVariantsGroupContainer>
            {rest.ids.map((id, idx) => (
              <Styled.VariantWrapper key={id}>
                <Styled.VariantContainer
                  onClick={onClickHandler(id)}
                  disabled={isLocked}
                  even={(rest.ids.length + idx + 1) % 2 === 0}
                >
                  <LabeledCheckbox
                    disabled={isLocked}
                    onChange={onClickHandler(id)}
                    defaultChecked={!!state[id].active}
                    readOnly
                    id={id}
                    label={state[id].title}
                  />
                </Styled.VariantContainer>
              </Styled.VariantWrapper>
            ))}
          </Styled.UnselectedVariantsGroupContainer>
        </Styled.VariantsList>
        <Styled.BottomControls>
          <Styled.CustomButton disabled={isEmpty} onClick={onReset} variant="text">
            {t('filters.resetFilter')}
          </Styled.CustomButton>
          {!filter && (
            <Styled.AmountLabel>{t('buttons.amountFrom', { from: selected.count, amount: total })}</Styled.AmountLabel>
          )}
        </Styled.BottomControls>
      </Styled.FilterWrapper>
    </Styled.CustomAnimationWrapper>
  );
};

export default memo(MultiSelectDropdown);
